import * as SeparatorPrimitive from '@radix-ui/react-separator';
import type { ToggleProps } from '@radix-ui/react-toggle';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as Sentry from '@sentry/react';
import { Document } from '@tiptap/extension-document';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Mention } from '@tiptap/extension-mention';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Text } from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import { generateText, type JSONContent } from '@tiptap/react';
import { format, parseISO } from 'date-fns';
import { produce } from 'immer';
import {
  BookOpenTextIcon,
  CalendarIcon,
  CheckIcon,
  CircleCheckBigIcon,
  CopyIcon,
  CornerDownRightIcon,
  DotIcon,
  ExternalLinkIcon,
  FileDownIcon,
  InfoIcon,
  PaperclipIcon,
  PlayIcon,
  RotateCwIcon,
  Share2Icon,
  SquarePenIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  TrendingDownIcon,
  TrendingUp,
  TriangleAlert,
  WorkflowIcon
} from 'lucide-react';
import type { MotionNodeAnimationOptions } from 'motion';
import * as motion from 'motion/react-client';
import {
  type Dispatch,
  type DOMAttributes,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useForm } from 'react-hook-form';
import { CartesianGrid, Line, LineChart, XAxis } from 'recharts';
import { toast } from 'sonner';

import CodeViewer from './CodeViewer';
import PromptField, { Tag } from './PromptField';
import ComboBoxField from './PromptField/ComboBoxField';
import DateField from './PromptField/DateField';
import NumberField from './PromptField/NumberField';
import SelectField from './PromptField/SelectField';
import TextField from './PromptField/TextField';
import textSerializers from './PromptField/textSerializers';

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@/components/ui/chart';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue
} from '@/components/ui/multi-select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  SearchableSelect,
  SearchableSelectContent,
  SearchableSelectGroup,
  SearchableSelectItem,
  SearchableSelectTrigger,
  SearchableSelectValue
} from '@/components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import NotionIcon from '@/icons/Notion';
import { useAuth } from '@/lib/auth';
import { findEntityById } from '@/lib/business-entities';
import { cn } from '@/lib/utils';

const textColor = {
  amber: 'text-amber-500',
  blue: 'text-blue-500',
  cyan: 'text-cyan-500',
  emerald: 'text-emerald-500',
  fuchsia: 'text-fuchsia-500',
  green: 'text-green-500',
  indigo: 'text-indigo-500',
  lime: 'text-lime-500',
  orange: 'text-orange-500',
  pink: 'text-pink-500',
  purple: 'text-purple-500',
  red: 'text-red-500',
  rose: 'text-rose-500',
  sky: 'text-sky-500',
  teal: 'text-teal-500',
  violet: 'text-violet-500',
  yellow: 'text-yellow-500'
};

const backgroundColor = {
  amber: 'bg-amber-500',
  blue: 'bg-blue-500',
  cyan: 'bg-cyan-500',
  emerald: 'bg-emerald-500',
  fuchsia: 'bg-fuchsia-500',
  green: 'bg-green-500',
  indigo: 'bg-indigo-500',
  lime: 'bg-lime-500',
  orange: 'bg-orange-500',
  pink: 'bg-pink-500',
  purple: 'bg-purple-500',
  red: 'bg-red-500',
  rose: 'bg-rose-500',
  sky: 'bg-sky-500',
  teal: 'bg-teal-500',
  violet: 'bg-violet-500',
  yellow: 'bg-yellow-500'
};

const borderColor = {
  amber: 'border-amber-500',
  blue: 'border-blue-500',
  cyan: 'border-cyan-500',
  emerald: 'border-emerald-500',
  fuchsia: 'border-fuchsia-500',
  green: 'border-green-500',
  indigo: 'border-indigo-500',
  lime: 'border-lime-500',
  orange: 'border-orange-500',
  pink: 'border-pink-500',
  purple: 'border-purple-500',
  red: 'border-red-500',
  rose: 'border-rose-500',
  sky: 'border-sky-500',
  teal: 'border-teal-500',
  violet: 'border-violet-500',
  yellow: 'border-yellow-500'
};

interface Props {
  businessEntities: BusinessEntity[];
  handlePresetSelectRef: RefObject<((label: string) => void) | null>;
  isLast: boolean;
  pending: boolean;
  presetPrompts: PresetPrompt[];
  promptAndAnswer: PromptAndAnswer;
  ref?: RefObject<HTMLDivElement | null>;
  sessionId: string;
  setPending: Dispatch<SetStateAction<boolean>>;
  setPromptsAndAnswers: Dispatch<SetStateAction<PromptAndAnswer[]>>;
  socket: null | WebSocket;
  userId: string;
}

const QuestionAnswer = ({
  businessEntities,
  handlePresetSelectRef,
  isLast,
  pending,
  presetPrompts,
  promptAndAnswer,
  ref,
  sessionId,
  setPending,
  setPromptsAndAnswers,
  socket,
  userId
}: Props) => {
  const form = useForm();
  const { getJWT } = useAuth();

  const isPending = pending && isLast;

  const containerRef = useRef<HTMLDivElement>(null);
  const reasonForDislikeTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [badAnswerToggleIsPressed, setBadAnswerToggleIsPressed] =
    useState(false);
  const [copyQueryIcon, setCopyQueryIcon] = useState(<CopyIcon />);
  const [error] = useState();
  const [goodAnswerToggleIsPressed, setGoodAnswerToggleIsPressed] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tabsValue, setTabsValue] = useState(isPending ? 'tasks' : 'answer');

  useEffect(() => {
    setTabsValue(isPending ? 'tasks' : 'answer');
  }, [isPending]);

  // Initialize form values for temporal fields when current_value is a custom date
  useEffect(() => {
    const inputFieldsTask = promptAndAnswer.tasks.find(
      task => task.type === 'input-fields'
    );

    if (inputFieldsTask?.type === 'input-fields') {
      const temporalRecommendations =
        inputFieldsTask.data.temporalRecommendations;

      if (temporalRecommendations) {
        temporalRecommendations.forEach(temporal => {
          if (temporal.type === 'select_or_date') {
            const stringOptions = temporal.options.filter(
              (opt): opt is string => typeof opt === 'string'
            );
            const isCustomDate =
              !stringOptions.includes(temporal.current_value) &&
              !isNaN(parseISO(temporal.current_value).valueOf());

            if (isCustomDate) {
              // Set the dropdown to "custom"
              form.setValue(`temporal_${temporal.field_name}`, 'custom', {
                shouldValidate: false
              });
              // Set the date picker to the parsed date
              try {
                const dateValue = parseISO(temporal.current_value);
                if (!isNaN(dateValue.valueOf())) {
                  form.setValue(
                    `temporal_${temporal.field_name}_date`,
                    dateValue,
                    { shouldValidate: false }
                  );
                }
              } catch {
                // If parsing fails, leave date field unset
              }
            }
          }
        });
      }
    }
  }, [form, promptAndAnswer.tasks]);

  const animate = useMemo<MotionNodeAnimationOptions['animate']>(
    () => ({ rotate: 90 }),
    []
  );

  const transition = useMemo<MotionNodeAnimationOptions['transition']>(
    () => ({
      damping: 15,
      delay: 0.2,
      duration: 1,
      mass: 1,
      repeat: Infinity,
      stiffness: 100,
      type: 'spring'
    }),
    []
  );

  const handleSubmitFeedbackButtonClick = useCallback<
    () => NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(
    () => async () => {
      setBadAnswerToggleIsPressed(true);

      const goodAnswerToggleWasPressed = goodAnswerToggleIsPressed;

      if (goodAnswerToggleIsPressed) {
        setGoodAnswerToggleIsPressed(false);
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/feedback/message`,
          {
            body: JSON.stringify({
              message_id: promptAndAnswer.id,
              rating: 'dislike',
              reason: reasonForDislikeTextareaRef.current?.value,
              session_id: sessionId,
              user_id: userId
            }),
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            method: 'POST'
          }
        );

        if (!response.ok) {
          throw new Error(
            `${response.status.toString()} (${response.statusText})`
          );
        }

        const json = (await response.json()) as {
          error: null | string;
          feedback_id: null | number;
          message: string;
          success: boolean;
        };

        if (json.error) {
          throw new Error(json.error);
        }
      } catch (error) {
        setBadAnswerToggleIsPressed(false);
        setGoodAnswerToggleIsPressed(goodAnswerToggleWasPressed);
        toast.error('Could not submit feedback. Please try again.', {
          position: 'top-right',
          richColors: true
        });
        Sentry.captureException(error);
      }
    },
    [goodAnswerToggleIsPressed, promptAndAnswer.id, sessionId, userId]
  );

  const handleConfirmButtonClick = useCallback<
    NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(() => {
    const values = form.getValues() as Record<string, unknown>;

    if (!socket) {
      throw new Error();
    }

    // Find the input-fields task to access clarifyingQuestions and temporalRecommendations data
    const inputFieldsTask = promptAndAnswer.tasks.find(
      task => task.type === 'input-fields'
    );

    void (async () => {
      const authData = await getJWT();

      if (!authData) {
        return;
      }

      // Build confirmed_entities from regular filters and required entity selectors
      const confirmedEntities: {
        field_name: string;
        value?: string;
        values?: string[];
      }[] = [];

      // Build confirmed_temporal from temporal fields
      const confirmedTemporal: {
        period_end?: string;
        period_length?: string;
      } = {};

      // Build clarifying_answers from clarifying question fields
      const clarifyingAnswers: {
        answer_type: 'context' | 'entity';
        entity_id?: string;
        entity_type?: string;
        other_text?: string;
        question_id: string;
        selected_option: string;
      }[] = [];

      // Get clarifying questions data from task for lookups
      const clarifyingQuestionsData =
        inputFieldsTask?.type === 'input-fields'
          ? inputFieldsTask.data.clarifyingQuestions
          : undefined;

      // Get temporal recommendations data from task
      const temporalRecommendationsData =
        inputFieldsTask?.type === 'input-fields'
          ? inputFieldsTask.data.temporalRecommendations
          : undefined;

      Object.entries(values).forEach(([key, value]) => {
        if (key.startsWith('clarifying_') && !key.endsWith('_other')) {
          const questionId = key.replace('clarifying_', '');
          const selectedOptionId = value as string;
          const otherText = values[`clarifying_${questionId}_other`] as
            | string
            | undefined;

          // Look up the question to get answer_type
          const question = clarifyingQuestionsData?.find(
            q => q.id === questionId
          );

          // If question uses frontend_entity_type, look up option from business entities
          let selectedOption: ClarifyingOption | undefined;
          let selectedOptionLabel: string;
          let entityId: string | undefined;
          let entityType: string | undefined;

          if (selectedOptionId === 'other') {
            selectedOptionLabel = 'Other (please specify)';
          } else {
            // First check if it's a special option like "none" in question.options
            selectedOption = question?.options.find(
              opt => opt.id === selectedOptionId
            );

            if (selectedOption) {
              // Option found in question.options (e.g., "none" type options)
              selectedOptionLabel = selectedOption.label;
              entityId = selectedOption.entity_id;
              entityType = selectedOption.entity_type;
            } else if (question?.frontend_entity_type) {
              // Options come from business entities
              const businessEntity = businessEntities.find(
                be => be.id === question.frontend_entity_type
              );
              const businessEntityItem = businessEntity?.items.find(
                item => item.name === selectedOptionId
              );

              if (businessEntityItem) {
                selectedOptionLabel = businessEntityItem.name;
                entityId = businessEntityItem.id;
                entityType = question.entity_type;
              } else {
                selectedOptionLabel = selectedOptionId; // Fallback
              }
            } else {
              selectedOptionLabel = selectedOptionId; // Fallback
            }
          }

          clarifyingAnswers.push({
            answer_type: question?.answer_type ?? 'context',
            // For "none" type options, set entity_id to "none" even if option doesn't have it
            entity_id:
              selectedOption?.type === 'none'
                ? 'none'
                : entityId ?? selectedOption?.entity_id,
            entity_type: entityType ?? selectedOption?.entity_type,
            other_text: selectedOptionId === 'other' ? otherText : undefined,
            question_id: questionId,
            selected_option: selectedOptionLabel
          });
        } else if (key.startsWith('temporal_') && !key.endsWith('_date')) {
          const fieldName = key.replace('temporal_', '');
          const dateValue = values[`temporal_${fieldName}_date`] as
            | Date
            | undefined;

          if (fieldName === 'Period End') {
            if (value === 'custom' && dateValue) {
              confirmedTemporal.period_end = format(dateValue, 'yyyy-MM-dd');
            } else {
              confirmedTemporal.period_end = value as string;
            }
          } else if (fieldName === 'Period Length') {
            confirmedTemporal.period_length = value as string;
          }
        } else if (
          !key.startsWith('clarifying_') &&
          !key.startsWith('temporal_')
        ) {
          // Regular entity filter or required entity selector
          if (Array.isArray(value)) {
            confirmedEntities.push({
              field_name: key,
              values: value as string[]
            });
          } else if (value) {
            confirmedEntities.push({
              field_name: key,
              value: value as string
            });
          }
        }
      });

      // Build the response data - keep 'filters' key for backward compatibility
      // and add new fields for temporal and clarifying answers
      const responseData: {
        access_token: string;
        clarifying_answers?: typeof clarifyingAnswers;
        confirmed_temporal?: {
          period_end: string;
          period_length: string;
        };
        filters: { field_name: string; value: string[] }[];
        session_id: string;
        user_id: string;
      } = {
        access_token: authData.accessToken,
        filters: confirmedEntities.map(entity => ({
          field_name: entity.field_name,
          value: entity.values ?? (entity.value ? [entity.value] : [])
        })),
        session_id: sessionId,
        user_id: authData.userId
      };

      // Add clarifying_answers if clarifying questions were shown and answered
      if (
        clarifyingQuestionsData &&
        clarifyingQuestionsData.length > 0 &&
        clarifyingAnswers.length > 0
      ) {
        responseData.clarifying_answers = clarifyingAnswers;
      }

      // Add confirmed_temporal if temporal recommendations were shown
      if (
        temporalRecommendationsData &&
        temporalRecommendationsData.length > 0
      ) {
        // Ensure we have default values if user didn't change them
        const periodEnd =
          confirmedTemporal.period_end ??
          temporalRecommendationsData.find(t => t.field_name === 'Period End')
            ?.current_value ??
          'Current';
        const periodLength =
          confirmedTemporal.period_length ??
          temporalRecommendationsData.find(
            t => t.field_name === 'Period Length'
          )?.current_value ??
          'LTM';

        responseData.confirmed_temporal = {
          period_end: periodEnd,
          period_length: periodLength
        };
      }

      socket.send(
        JSON.stringify({
          data: responseData,
          message_id: promptAndAnswer.id,
          type: 'filters'
        })
      );

      setPromptsAndAnswers(promptsAndAnswers =>
        produce(promptsAndAnswers, draft => {
          draft[draft.length - 1].tasks = draft[draft.length - 1].tasks.filter(
            task => task.type !== 'input-fields'
          );
        })
      );
    })();
  }, [
    form,
    getJWT,
    promptAndAnswer,
    sessionId,
    setPromptsAndAnswers,
    socket,
    businessEntities
  ]);

  const handleCopyButtonClick = useCallback<
    NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {},
    []
  );

  const handleCopyQueryButtonClick = useCallback<
    (content: JSONContent) => DOMAttributes<HTMLButtonElement>['onClick']
  >(
    content => () => {
      try {
        void navigator.clipboard.writeText(
          generateText(
            content,
            [
              Document,
              Paragraph,
              Text,
              HardBreak,
              UndoRedo,
              Placeholder.configure({ placeholder: 'Ask anything' }),
              Tag,
              Mention,
              ComboBoxField,
              DateField,
              NumberField,
              TextField,
              SelectField
            ],
            { textSerializers }
          )
        );
      } catch (error) {
        console.error(error);
      }

      setCopyQueryIcon(<CheckIcon />);

      setTimeout(() => {
        setCopyQueryIcon(<CopyIcon />);
      }, 2000);
    },
    []
  );

  const handleEditQueryButtonClick = useCallback<
    NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(() => {
    setIsEditing(true);
  }, []);

  const handleExportButtonClick = useCallback<
    NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {},
    []
  );

  const handleFollowUpPromptButtonClick = useCallback<
    NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {},
    []
  );

  const handleGoodAnswerTogglePressedChange = useCallback<
    () => NonNullable<ToggleProps['onPressedChange']>
  >(
    () => async pressed => {
      setGoodAnswerToggleIsPressed(pressed);

      if (pressed) {
        const badAnswerToggleWasPressed = badAnswerToggleIsPressed;

        if (badAnswerToggleIsPressed) {
          setBadAnswerToggleIsPressed(false);
        }

        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/feedback/message`,
            {
              body: JSON.stringify({
                message_id: promptAndAnswer.id,
                rating: 'like',
                session_id: sessionId,
                user_id: userId
              }),
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              },
              method: 'POST'
            }
          );

          if (!response.ok) {
            throw new Error(
              `${response.status.toString()} (${response.statusText})`
            );
          }

          const json = (await response.json()) as {
            error: null | string;
            feedback_id: null | number;
            message: string;
            success: boolean;
          };

          if (json.error) {
            throw new Error(json.error);
          }
        } catch (error) {
          setGoodAnswerToggleIsPressed(false);
          setBadAnswerToggleIsPressed(badAnswerToggleWasPressed);
          toast.error('Could not submit feedback. Please try again.', {
            position: 'top-right',
            richColors: true
          });
          Sentry.captureException(error);
        }
      }
    },
    [badAnswerToggleIsPressed, promptAndAnswer.id, sessionId, userId]
  );

  const handleShareButtonClick = useCallback<
    NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {},
    []
  );

  const isPaused = promptAndAnswer.tasks.find(
    task => task.type === 'input-fields'
  );

  const animationProps = useMemo(
    () => ({
      animate: { opacity: 1 },
      initial: { opacity: isPending ? 0 : 1 }
    }),
    [isPending]
  );

  return (
    <div
      className="w-[640px] max-w-full pt-6 lg:w-[720px]"
      key={promptAndAnswer.id}
      ref={ref}
    >
      {isEditing ? (
        <PromptField
          businessEntities={businessEntities}
          handlePresetSelectRef={handlePresetSelectRef}
          initialPrompt={promptAndAnswer.prompt}
          messageId={promptAndAnswer.id}
          pending={pending}
          presetPrompts={presetPrompts}
          sessionId={sessionId}
          setIsEditing={setIsEditing}
          setPending={setPending}
          setPromptsAndAnswers={setPromptsAndAnswers}
          socket={socket}
          state="editing"
          userId={userId}
        />
      ) : (
        <div className="border-input group hover:bg-alpha-90 relative mb-4 rounded-xl border p-4 transition-colors">
          <PromptField
            handlePresetSelectRef={handlePresetSelectRef}
            initialPrompt={promptAndAnswer.prompt}
            state="read-only"
          />
          <div
            className="absolute right-2 -bottom-5 flex gap-x-2 opacity-0 transition-opacity group-hover:opacity-100 has-[*:focus-visible]:opacity-100"
            ref={containerRef}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="backdrop-blur-xs"
                  disabled={pending}
                  onClick={handleEditQueryButtonClick}
                  size="icon"
                  variant="outline"
                >
                  <SquarePenIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent container={containerRef.current}>
                Edit Query
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="backdrop-blur-xs"
                  onClick={handleCopyQueryButtonClick(promptAndAnswer.prompt)}
                  size="icon"
                  variant="outline"
                >
                  {copyQueryIcon}
                </Button>
              </TooltipTrigger>
              <TooltipContent container={containerRef.current}>
                Copy Query
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {error && (
        <Alert>
          <TriangleAlert />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
          <Button>
            <RotateCwIcon />
            Retry
          </Button>
        </Alert>
      )}
      <Tabs
        onValueChange={setTabsValue}
        value={tabsValue}
      >
        <TabsList>
          {!isPending && (
            <>
              <TabsTrigger value="answer">
                <CircleCheckBigIcon />
                Answer
              </TabsTrigger>
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-binary-expression */}
              {false && (
                <TabsTrigger value="sources">
                  <PaperclipIcon />
                  Sources · 3
                </TabsTrigger>
              )}
            </>
          )}
          <TabsTrigger value="tasks">
            <WorkflowIcon />
            Tasks
          </TabsTrigger>
        </TabsList>
        <TabsContent
          className="space-y-4 overflow-y-auto"
          value="answer"
        >
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {(promptAndAnswer.answer?.blocks ?? []).map(item => {
            if (item.type === 'heading') {
              return (
                <div
                  className="text-lg font-medium"
                  key={item.id}
                >
                  {item.data.heading}
                </div>
              );
            }

            if (item.type === 'paragraph') {
              return (
                <div key={item.id}>
                  {item.data.paragraph}{' '}
                  {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-binary-expression */}
                  {false && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="bg-secondary hover:bg-brand transition-color group ml-2 inline-flex gap-x-1 rounded-full px-2 py-0.5"
                          data-slot="button"
                          type="button"
                        >
                          <div className="group-hover:text-primary-foreground text-xs font-medium">
                            Notion
                          </div>
                          <div className="text-muted-foreground group-hover:text-primary-foreground text-xs font-semibold">
                            +1
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content
                          align="start"
                          className="bg-input dark:input/80 text-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md border p-2.5 text-balance backdrop-blur-3xl"
                          data-slot="tooltip-content"
                          side="bottom"
                          sideOffset={8}
                        >
                          <div className="text-muted-foreground mb-2 text-xs font-medium">
                            2 Sources
                          </div>
                          <div className="bg-alpha-60 hover:bg-alpha-40 mb-2 rounded-sm p-3">
                            <div className="mb-2 flex items-center gap-x-2 text-sm">
                              <NotionIcon size={14} />
                              Notion
                            </div>
                            <div className="mb-1 font-medium">
                              Title of Document
                            </div>
                            <div className="text-muted-foreground line-clamp-2 w-[373px] text-sm font-light">
                              Lorem ipsum dolor sit amet consectetur. Morbi sem
                              egestas egestas molestie. Condimentum non pulvinar
                              in nec tempus arcu sapien egestas ullamcorper.
                              Vulputate sed vel ultrices vitae quis nisl. Magna
                              habitant adipiscing mollis tristique proin nibh. A
                              turpis ullamcorper adipiscing elementum
                              ullamcorper. Penatibus bibendum diam eget tempor.
                              Sed sit iaculis sagittis pellentesque in. Auctor
                              etiam vivamus mattis vel. Turpis feugiat
                              pellentesque risus fringilla volutpat sapien
                              adipiscing pellentesque. In non sed consequat
                              posuere neque lorem. Odio amet etiam posuere sed.
                            </div>
                          </div>
                          <div className="bg-alpha-60 hover:bg-alpha-40 rounded-sm p-3">
                            <div className="mb-2 flex items-center gap-x-2 text-sm">
                              <NotionIcon size={14} />
                              Notion
                            </div>
                            <div className="mb-1 font-medium">
                              Title of Document
                            </div>
                            <div className="text-muted-foreground line-clamp-2 w-[373px] text-sm font-light">
                              Lorem ipsum dolor sit amet consectetur. Morbi sem
                              egestas egestas molestie. Condimentum non pulvinar
                              in nec tempus arcu sapien egestas ullamcorper.
                              Vulputate sed vel ultrices vitae quis nisl. Magna
                              habitant adipiscing mollis tristique proin nibh. A
                              turpis ullamcorper adipiscing elementum
                              ullamcorper. Penatibus bibendum diam eget tempor.
                              Sed sit iaculis sagittis pellentesque in. Auctor
                              etiam vivamus mattis vel. Turpis feugiat
                              pellentesque risus fringilla volutpat sapien
                              adipiscing pellentesque. In non sed consequat
                              posuere neque lorem. Odio amet etiam posuere sed.
                            </div>
                          </div>
                        </TooltipPrimitive.Content>
                      </TooltipPrimitive.Portal>
                    </Tooltip>
                  )}
                </div>
              );
            }

            if (item.type === 'list') {
              return (
                <div key={item.id}>
                  <p className="pb-1.5">
                    <b className="font-semibold">{item.data.title}</b>:
                  </p>
                  <ul>
                    {item.data.list.map(item => (
                      <li
                        className="flex pb-1.5"
                        key={item}
                      >
                        <DotIcon className="shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }

            if (item.type === 'table') {
              return (
                <Table key={item.id}>
                  <TableHeader>
                    <TableRow>
                      {item.data.columns.map(column => (
                        <TableHead key={column.id}>{column.column}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {item.data.rows.map(row => (
                      <TableRow key={row.id}>
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>{cell.cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            }

            if (item.type === 'link-group') {
              return (
                <div
                  className="space-y-2"
                  key={item.id}
                >
                  {item.data.links.map(({ description, id, link, name }) => (
                    <a
                      className="bg-custom-background dark:bg-input/30 hover:bg-accent dark:hover:bg-input/50 transition-color flex rounded-md border p-3"
                      href={link}
                      key={id}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      <div className="grow">
                        <div className="mb-2 flex items-center gap-x-2 pl-5">
                          {/* <NotionIcon size={14} /> */}
                          <div className="text-sm leading-none">{name}</div>
                        </div>
                        <div className="text-muted-foreground pl-5.5 text-sm leading-none font-light">
                          {description}
                        </div>
                      </div>
                      <ExternalLinkIcon
                        className="text-muted-foreground self-center"
                        size={16}
                      />
                    </a>
                  ))}
                </div>
              );
            }

            if (item.type === 'metric') {
              return (
                <div
                  className="bg-card rounded-lg border"
                  key={item.id}
                >
                  <div className="text-muted-foreground flex justify-between border-b px-6 py-3.5">
                    <div className="leading-none">{item.data.title}</div>
                    {item.data.powerBiLink && (
                      <a
                        className="flex items-center gap-x-2"
                        href={item.data.powerBiLink}
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        <span className="text-sm leading-none underline">
                          View in PowerBI
                        </span>
                        <ExternalLinkIcon size={16} />
                      </a>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="mb-5 flex items-end gap-x-2">
                      <div className="text-4xl font-medium">
                        {item.data.amount}
                      </div>
                      <div className="text-muted-foreground text-lg">
                        {item.data.currency}
                      </div>
                    </div>
                    <div className="text-lg font-medium">
                      {item.data.company}
                    </div>
                    {item.data.date && (
                      <div className="text-muted-foreground mt-1 flex items-center gap-x-1.5">
                        <CalendarIcon size={16} />
                        <div className="leading-none">{item.data.date}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            if (item.type === 'chart') {
              return (
                <div
                  className="bg-card rounded-lg border"
                  key={item.id}
                >
                  <div className="text-muted-foreground flex justify-between border-b px-6 py-3.5">
                    <div className="leading-none">{item.data.title}</div>
                    {item.data.powerBiLink && (
                      <a
                        className="flex items-center gap-x-2"
                        href={item.data.powerBiLink}
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        <span className="text-sm leading-none underline">
                          View in PowerBI
                        </span>
                        <ExternalLinkIcon size={16} />
                      </a>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="mb-1 text-lg font-medium">
                      {item.data.chartTitle}
                    </div>
                    <div className="text-muted-foreground mb-6 leading-none">
                      {item.data.chartDate}
                    </div>
                    <ChartContainer
                      className="mb-6"
                      config={{
                        [item.data.chartLineDataKey]: {
                          color: 'var(--chart-1)',
                          label: item.data.chartLineLabel
                        }
                      }}
                    >
                      <LineChart
                        accessibilityLayer
                        data={item.data.chartData}
                        margin={{
                          left: 12,
                          right: 12
                        }}
                      >
                        <CartesianGrid vertical={false} />
                        <XAxis
                          axisLine={false}
                          dataKey={item.data.chartXAxisDataKey}
                          tickLine={false}
                          tickMargin={8}
                        />
                        <ChartTooltip
                          content={<ChartTooltipContent hideLabel />}
                          cursor={false}
                        />
                        <Line
                          dataKey={item.data.chartLineDataKey}
                          dot={false}
                          stroke="var(--color-desktop)"
                          strokeWidth={2}
                          type="natural"
                        />
                      </LineChart>
                    </ChartContainer>
                    <div className="mb-2 flex items-center gap-x-2">
                      <div className="text-sm leading-none font-medium">
                        {item.data.chartTrend}
                      </div>
                      {item.data.chartTrendDirection === 'DOWN' ? (
                        <TrendingDownIcon size={16} />
                      ) : (
                        <TrendingUp size={16} />
                      )}
                    </div>
                    <div className="text-muted-foreground text-sm leading-none">
                      {item.data.chartDescription}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <SeparatorPrimitive.Root
                className="bg-border box-content h-px w-full shrink-0 bg-clip-content py-4"
                data-slot="separator"
                decorative
                key={item.id}
                orientation="horizontal"
              />
            );
          })}
          <div className="pt-2">
            <div className="mb-2 flex gap-x-1">
              {/* eslint-disable-next-line no-constant-binary-expression, @typescript-eslint/no-unnecessary-condition */}
              {false && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="text-muted-foreground dark:hover:bg-accent hover:bg-accent rounded-sm backdrop-blur-xs"
                      onClick={handleCopyButtonClick}
                      size="icon"
                      variant="ghost"
                    >
                      <CopyIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Toggle
                      aria-label="Toggle thumbs up button"
                      className="text-muted-foreground dark:hover:bg-accent hover:bg-accent rounded-sm backdrop-blur-xs"
                      onPressedChange={handleGoodAnswerTogglePressedChange()}
                      pressed={goodAnswerToggleIsPressed}
                    >
                      <ThumbsUpIcon />
                    </Toggle>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Good Answer</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    {badAnswerToggleIsPressed ? (
                      <Toggle
                        aria-label="Toggle thumbs down button"
                        className="text-muted-foreground dark:hover:bg-accent hover:bg-accent rounded-sm backdrop-blur-xs"
                        onClick={() => {
                          setBadAnswerToggleIsPressed(false);
                        }}
                        pressed
                      >
                        <ThumbsDownIcon />
                      </Toggle>
                    ) : (
                      <Dialog>
                        <DialogTrigger asChild>
                          <span>
                            <Toggle
                              aria-label="Toggle thumbs down button"
                              className="text-muted-foreground dark:hover:bg-accent hover:bg-accent rounded-sm backdrop-blur-xs"
                              pressed={false}
                            >
                              <ThumbsDownIcon />
                            </Toggle>
                          </span>
                        </DialogTrigger>
                        <DialogContent aria-describedby={undefined}>
                          <DialogHeader>
                            <DialogTitle>Provide Feedback</DialogTitle>
                          </DialogHeader>

                          <DialogBody>
                            <div className="flex items-center gap-2">
                              <div className="grid flex-1 gap-2">
                                <Label htmlFor="ohyai1Weex">
                                  Reason for dislike
                                </Label>
                                <Textarea
                                  id="ohyai1Weex"
                                  ref={reasonForDislikeTextareaRef}
                                  required
                                />
                              </div>
                            </div>
                          </DialogBody>

                          <DialogFooter>
                            <DialogClose asChild>
                              <Button
                                type="button"
                                variant="secondary"
                              >
                                Close
                              </Button>
                            </DialogClose>
                            <DialogClose asChild>
                              <Button
                                onClick={handleSubmitFeedbackButtonClick()}
                                type="button"
                              >
                                Submit Feedback
                              </Button>
                            </DialogClose>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>Bad Answer</TooltipContent>
              </Tooltip>
              {/* eslint-disable-next-line no-constant-binary-expression, @typescript-eslint/no-unnecessary-condition */}
              {false && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="text-muted-foreground dark:hover:bg-accent hover:bg-accent rounded-sm backdrop-blur-xs"
                      onClick={handleExportButtonClick}
                      size="icon"
                      variant="ghost"
                    >
                      <FileDownIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Export</TooltipContent>
                </Tooltip>
              )}
              {/* eslint-disable-next-line no-constant-binary-expression, @typescript-eslint/no-unnecessary-condition */}
              {false && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="text-muted-foreground dark:hover:bg-accent hover:bg-accent rounded-sm backdrop-blur-xs"
                      onClick={handleShareButtonClick}
                      size="icon"
                      variant="ghost"
                    >
                      <Share2Icon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Share</TooltipContent>
                </Tooltip>
              )}
            </div>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-binary-expression */}
            {false && (
              <div>
                {[
                  'Lorem ipsum dolor sit amet consectetur',
                  'At dapibus laoreet ipsum condimentum dictum tempor suspendisse',
                  'Nunc diam adipiscing dignissim in arcu venenatis amet vitae auctor nisl iaculis'
                ].map(prompt => (
                  <button
                    className="text-muted-foreground hover:text-foreground transition-color flex cursor-default items-center gap-x-2 py-1 text-left hover:underline"
                    key={prompt}
                    onClick={handleFollowUpPromptButtonClick}
                    type="button"
                  >
                    <CornerDownRightIcon size={16} />
                    {prompt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-binary-expression */}
        {false && (
          <TabsContent
            className="space-y-3"
            value="sources"
          >
            {[
              {
                description:
                  'Lorem ipsum dolor sit amet consectetur. Morbi sem egestas egestas molestie. Condimentum non pulvinar in nec tempus arcu sapien egestas ullamcorper. Vulputate sed vel ultrices vitae quis nisl. Magna habitant adipiscing mollis tristique proin nibh. A turpis ullamcorper adipiscing elementum ullamcorper. Penatibus bibendum diam eget tempor. Sed sit iaculis sagittis pellentesque in. Auctor etiam vivamus mattis vel. Turpis feugiat pellentesque risus fringilla volutpat sapien adipiscing pellentesque. In non sed consequat posuere neque lorem. Odio amet etiam posuere sed',
                id: '1',
                title: 'Title of Document'
              },
              {
                description:
                  'Lorem ipsum dolor sit amet consectetur. Morbi sem egestas egestas molestie. Condimentum non pulvinar in nec tempus arcu sapien egestas ullamcorper. Vulputate sed vel ultrices vitae quis nisl. Magna habitant adipiscing mollis tristique proin nibh. A turpis ullamcorper adipiscing elementum ullamcorper. Penatibus bibendum diam eget tempor. Sed sit iaculis sagittis pellentesque in. Auctor etiam vivamus mattis vel. Turpis feugiat pellentesque risus fringilla volutpat sapien adipiscing pellentesque. In non sed consequat posuere neque lorem. Odio amet etiam posuere sed.',
                id: '2',
                title: 'Title of Document'
              },
              {
                description:
                  'Lorem ipsum dolor sit amet consectetur. Morbi sem egestas egestas molestie. Condimentum non pulvinar in nec tempus arcu sapien egestas ullamcorper. Vulputate sed vel ultrices vitae quis nisl. Magna habitant adipiscing mollis tristique proin nibh. A turpis ullamcorper adipiscing elementum ullamcorper. Penatibus bibendum diam eget tempor. Sed sit iaculis sagittis pellentesque in. Auctor etiam vivamus mattis vel. Turpis feugiat pellentesque risus fringilla volutpat sapien adipiscing pellentesque. In non sed consequat posuere neque lorem. Odio amet etiam posuere sed.',
                id: '3',
                title: 'Title of Document'
              }
            ].map(({ description, id, title }) => (
              <div
                className="hover:bg-input/50 group bg-background dark:bg-input/30 rounded-md p-3 transition-colors"
                key={id}
              >
                <div className="mb-2 flex items-center gap-x-2 text-sm leading-3.5">
                  <NotionIcon size={14} />
                  Notion
                </div>
                <div className="mb-1 font-medium">{title}</div>
                <div className="text-muted-foreground group-hover:text-foreground line-clamp-2 text-sm font-light">
                  {description}
                </div>
              </div>
            ))}
          </TabsContent>
        )}
        <TabsContent
          className="space-y-2"
          value="tasks"
        >
          {isPending ? (
            isPaused ? (
              <div className="flex items-center gap-x-2 pb-2 text-sm font-medium text-orange-600 dark:text-orange-400">
                <svg
                  fill="none"
                  height="16"
                  viewBox="0 0 16 16"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    fill="currentColor"
                    height="11"
                    width="3"
                    x="3"
                    y="2.5"
                  />
                  <rect
                    fill="currentColor"
                    height="11"
                    width="3"
                    x="10"
                    y="2.5"
                  />
                </svg>
                Paused
              </div>
            ) : (
              <div className="text-brand flex items-center gap-x-2 pb-2 text-sm font-medium">
                <motion.svg
                  animate={animate}
                  fill="none"
                  height="16"
                  transition={transition}
                  viewBox="0 0 16 16"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    fill="currentColor"
                    height="11"
                    width="11"
                    x="2.5"
                    y="2.5"
                  />
                </motion.svg>
                Thinking...
              </div>
            )
          ) : (
            promptAndAnswer.thinkingDuration && (
              <div className="pb-2 text-sm font-medium">{`Thought for ${promptAndAnswer.thinkingDuration}:`}</div>
            )
          )}
          {promptAndAnswer.tasks.map(task =>
            task.type === 'applied-filters' ? (
              <motion.div
                className="flex gap-x-2 text-sm"
                key={task.id}
                {...animationProps}
              >
                <div className="flex shrink-0 gap-x-2">
                  <div className="bg-outline mx-2 my-0.5 w-0.25" />
                  <div className="text-muted-foreground flex h-7 items-center gap-x-1">
                    {task.data.icon}
                    <div className="font-medium">{task.data.title}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {task.data.appliedFilters.map(appliedFilter => {
                    const color = businessEntities.find(
                      businessEntity => businessEntity.id === appliedFilter.type
                    )?.color as keyof typeof textColor;

                    return (
                      <div
                        className={cn(
                          'rounded-xl border bg-linear-[var(--alpha-30)] px-2 py-0.75 text-sm whitespace-nowrap',
                          textColor[color],
                          backgroundColor[color],
                          borderColor[color]
                        )}
                        key={appliedFilter.filter}
                      >
                        {appliedFilter.filter}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ) : task.type === 'code-editor' ? (
              <CodeViewer
                animationProps={animationProps}
                key={task.id}
                task={task}
              />
            ) : task.type === 'default' ? (
              <motion.div
                className="flex gap-x-2 text-sm"
                key={task.id}
                {...animationProps}
              >
                <div className="bg-outline mx-2 my-0.5 w-0.25" />
                <div className="text-muted-foreground flex items-start gap-x-1 leading-5">
                  {task.data.icon}
                  <div>
                    <div className="flex items-center font-medium">
                      {task.data.title}
                      {task.data.tooltip && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className="ml-1 size-4 rounded-full"
                              size="icon"
                              variant="outline"
                            >
                              <InfoIcon />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {task.data.tooltip.map(tooltip => (
                                <span
                                  className="block"
                                  key={tooltip}
                                >
                                  {tooltip}
                                </span>
                              ))}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    {task.data.description && (
                      <div className="mt-1 text-zinc-400 dark:text-zinc-500">
                        {task.data.description}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : task.type === 'input-fields' ? (
              <motion.div
                className="py-4"
                key={task.id}
                {...animationProps}
              >
                <div className="mb-4">{task.data.instructions}</div>
                <div className="flex gap-x-2 text-sm">
                  <div className="bg-outline mx-2 my-0.5 w-0.25" />
                  <Form {...form}>
                    <form className="bg-custom-background dark:bg-input/50 border-outline dark:border-input-dark w-full rounded-lg border px-4 pt-6 pb-4 shadow-xl">
                      {/* Section 1: Clarifying Questions */}
                      {task.data.clarifyingQuestions &&
                        task.data.clarifyingQuestions.length > 0 && (
                          <div className="mb-6">
                            <h4 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                              Help us understand your query
                            </h4>
                            <div className="grid grid-cols-1 gap-4">
                              {task.data.clarifyingQuestions.map(question => {
                                // If frontend_entity_type is provided, populate options from business entities
                                const entityResult = question.frontend_entity_type
                                  ? findEntityById(
                                      businessEntities,
                                      question.frontend_entity_type
                                    )
                                  : null;

                                // Combine business entity items with special options from question.options (e.g., "none", "custom")
                                const businessEntityOptions = entityResult
                                  ? entityResult.items.map(item => ({
                                      id: item.name,
                                      label: item.name
                                    }))
                                  : [];

                                // Special options (like "none", "custom", etc.) should always be included from question.options
                                // These are options that have a type field, indicating they're special/custom options
                                const specialOptions = question.options.filter(
                                  opt => opt.type !== undefined
                                );

                                const optionsToRender = entityResult
                                  ? [...specialOptions, ...businessEntityOptions]
                                  : question.options;

                                return (
                                  <FormField
                                    control={form.control}
                                    key={question.id}
                                    name={`clarifying_${question.id}`}
                                    render={({ field }) => (
                                      <FormItem className="w-full">
                                        <FormLabel>
                                          {question.question}
                                          {question.required && (
                                            <span className="text-destructive ml-1">
                                              *
                                            </span>
                                          )}
                                        </FormLabel>
                                        <SearchableSelect
                                          onValueChange={field.onChange}
                                          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                          value={field.value}
                                        >
                                          <FormControl>
                                            <SearchableSelectTrigger className="w-full">
                                              <SearchableSelectValue placeholder="Select an option..." />
                                            </SearchableSelectTrigger>
                                          </FormControl>
                                          <SearchableSelectContent>
                                            <SearchableSelectGroup>
                                              {optionsToRender.map(opt => (
                                                <SearchableSelectItem
                                                  key={opt.id}
                                                  value={opt.id}
                                                >
                                                  {opt.label}
                                                </SearchableSelectItem>
                                              ))}
                                              {question.allow_other && (
                                                <SearchableSelectItem value="other">
                                                  Other (please specify)
                                                </SearchableSelectItem>
                                              )}
                                            </SearchableSelectGroup>
                                          </SearchableSelectContent>
                                        </SearchableSelect>
                                      {}
                                      {field.value === 'other' && (
                                        <FormField
                                          control={form.control}
                                          name={`clarifying_${question.id}_other`}
                                          render={({ field: otherField }) => (
                                            <FormItem className="mt-2">
                                              <FormControl>
                                                <Input
                                                  placeholder="Please specify..."
                                                  {...otherField}
                                                />
                                              </FormControl>
                                            </FormItem>
                                          )}
                                        />
                                      )}
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              );
                              })}
                            </div>
                          </div>
                        )}

                      {/* Section 2: Required Entity Selectors */}
                      {task.data.requiredEntitySelectors &&
                        task.data.requiredEntitySelectors.length > 0 && (
                          <div className="mb-6">
                            <h4 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                              Select Required Information
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              {task.data.requiredEntitySelectors.map(
                                selector => {
                                  const entityResult = findEntityById(
                                    businessEntities,
                                    selector.options[0]
                                  );

                                  return (
                                    <FormField
                                      control={form.control}
                                      key={selector.field_name}
                                      name={selector.field_name}
                                      render={({ field }) => (
                                        <FormItem className="w-full">
                                          <FormLabel>
                                            {selector.field_name}
                                            {selector.required && (
                                              <span className="text-destructive ml-1">
                                                *
                                              </span>
                                            )}
                                          </FormLabel>
                                          {selector.reason && (
                                            <p className="text-muted-foreground mb-1 text-xs">
                                              {selector.reason}
                                            </p>
                                          )}
                                          {selector.type === 'multi-select' ? (
                                            <MultiSelect
                                              onValuesChange={field.onChange}
                                              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                              values={field.value}
                                            >
                                              <MultiSelectTrigger className="w-full">
                                                <MultiSelectValue
                                                  placeholder={
                                                    selector.description ??
                                                    'Select...'
                                                  }
                                                />
                                              </MultiSelectTrigger>
                                              <MultiSelectContent>
                                                <MultiSelectGroup>
                                                  {entityResult?.items.map(
                                                    item => (
                                                      <MultiSelectItem
                                                        key={item.name}
                                                        value={item.name}
                                                      >
                                                        {item.name}
                                                      </MultiSelectItem>
                                                    )
                                                  )}
                                                </MultiSelectGroup>
                                              </MultiSelectContent>
                                            </MultiSelect>
                                          ) : (
                                            <SearchableSelect
                                              onValueChange={field.onChange}
                                              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                              value={field.value}
                                            >
                                              <FormControl>
                                                <SearchableSelectTrigger className="w-full">
                                                  <SearchableSelectValue
                                                    placeholder={
                                                      selector.description ??
                                                      'Select...'
                                                    }
                                                  />
                                                </SearchableSelectTrigger>
                                              </FormControl>
                                              <SearchableSelectContent>
                                                <SearchableSelectGroup>
                                                  {entityResult?.items.map(
                                                    item => (
                                                      <SearchableSelectItem
                                                        key={item.name}
                                                        value={item.name}
                                                      >
                                                        {item.name}
                                                      </SearchableSelectItem>
                                                    )
                                                  )}
                                                </SearchableSelectGroup>
                                              </SearchableSelectContent>
                                            </SearchableSelect>
                                          )}
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}

                      {/* Section 3: Entity Filters (Existing) */}
                      {task.data.filters.length > 0 && (
                        <div className="mb-6">
                          {((task.data.clarifyingQuestions?.length ?? 0) > 0 ||
                            (task.data.requiredEntitySelectors?.length ?? 0) >
                              0 ||
                            (task.data.temporalRecommendations?.length ?? 0) >
                              0) && (
                            <h4 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                              Confirm Entity Filters
                            </h4>
                          )}
                          <div className="grid grid-cols-2 gap-4">
                            {task.data.filters.map(filter =>
                              filter.type === 'date' ? (
                                <FormField
                                  control={form.control}
                                  defaultValue={filter.defaultValue}
                                  key={filter.name}
                                  name={filter.name}
                                  render={({ field }) => (
                                    <FormItem className="flex w-full flex-col">
                                      <FormLabel>{filter.label}</FormLabel>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <FormControl>
                                            <Button
                                              className={cn(
                                                'w-full pl-3 text-left font-normal',
                                                !field.value &&
                                                  'text-muted-foreground'
                                              )}
                                              variant={'outline'}
                                            >
                                              {field.value ? (
                                                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                                                format(field.value, 'PPP')
                                              ) : (
                                                <span>Pick a date</span>
                                              )}
                                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                            </Button>
                                          </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent
                                          align="start"
                                          className="w-auto p-0"
                                        >
                                          <Calendar
                                            captionLayout="dropdown"
                                            mode="single"
                                            onSelect={field.onChange}
                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                            selected={field.value}
                                          />
                                        </PopoverContent>
                                      </Popover>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              ) : filter.type === 'multi-select' ? (
                                <FormField
                                  control={form.control}
                                  defaultValue={filter.defaultValues}
                                  key={filter.name}
                                  name={filter.name}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>{filter.label}</FormLabel>
                                      <MultiSelect
                                        onValuesChange={field.onChange}
                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                        values={field.value}
                                      >
                                        <MultiSelectTrigger className="w-full">
                                          <MultiSelectValue />
                                        </MultiSelectTrigger>
                                        <MultiSelectContent>
                                          <MultiSelectGroup>
                                            {filter.options.map(option => (
                                              <MultiSelectItem
                                                key={option}
                                                value={option}
                                              >
                                                {option}
                                              </MultiSelectItem>
                                            ))}
                                          </MultiSelectGroup>
                                        </MultiSelectContent>
                                      </MultiSelect>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              ) : (
                                <FormField
                                  control={form.control}
                                  defaultValue={filter.defaultValue}
                                  key={filter.name}
                                  name={filter.name}
                                  render={({ field }) => (
                                    <FormItem className="w-full">
                                      <FormLabel>{filter.label}</FormLabel>
                                      <SearchableSelect
                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                        defaultValue={field.value}
                                        onValueChange={field.onChange}
                                      >
                                        <FormControl>
                                          <SearchableSelectTrigger className="w-full">
                                            <SearchableSelectValue />
                                          </SearchableSelectTrigger>
                                        </FormControl>
                                        <SearchableSelectContent>
                                          <SearchableSelectGroup>
                                            {filter.options.map(option => (
                                              <SearchableSelectItem
                                                key={option}
                                                value={option}
                                              >
                                                {option}
                                              </SearchableSelectItem>
                                            ))}
                                          </SearchableSelectGroup>
                                        </SearchableSelectContent>
                                      </SearchableSelect>
                                    </FormItem>
                                  )}
                                />
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* Section 4: Temporal Context */}
                      {task.data.temporalRecommendations &&
                        task.data.temporalRecommendations.length > 0 && (
                          <div className="mb-6">
                            <h4 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                              Time Period
                            </h4>
                            <div className="grid grid-cols-2 gap-4 items-start">
                              {task.data.temporalRecommendations.map(
                                temporal => (
                                  <FormField
                                    control={form.control}
                                    defaultValue={temporal.current_value}
                                    key={temporal.field_name}
                                    name={`temporal_${temporal.field_name}`}
                                    render={({ field }) => (
                                      <FormItem className="w-full">
                                        <FormLabel>
                                          {temporal.field_name}
                                        </FormLabel>
                                        {temporal.description && (
                                          <p className="text-muted-foreground mb-1 text-xs">
                                            {temporal.description}
                                          </p>
                                        )}
                                        {temporal.type === 'select_or_date' ? (
                                          <>
                                            <SearchableSelect
                                              onValueChange={field.onChange}
                                              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                              value={field.value}
                                            >
                                              <FormControl>
                                                <SearchableSelectTrigger className="w-full">
                                                  <SearchableSelectValue />
                                                </SearchableSelectTrigger>
                                              </FormControl>
                                              <SearchableSelectContent>
                                                <SearchableSelectGroup>
                                                  {temporal.options.map(opt =>
                                                    typeof opt === 'string' ? (
                                                      <SearchableSelectItem
                                                        key={opt}
                                                        value={opt}
                                                      >
                                                        {opt}
                                                      </SearchableSelectItem>
                                                    ) : null
                                                  )}
                                                  <SearchableSelectItem value="custom">
                                                    Custom Date...
                                                  </SearchableSelectItem>
                                                </SearchableSelectGroup>
                                              </SearchableSelectContent>
                                            </SearchableSelect>
                                            {field.value === 'custom' && (
                                              <FormField
                                                control={form.control}
                                                name={`temporal_${temporal.field_name}_date`}
                                                render={({
                                                  field: dateField
                                                }) => (
                                                  <FormItem className="mt-1 flex flex-col">
                                                    <Popover>
                                                      <PopoverTrigger asChild>
                                                        <FormControl>
                                                          <Button
                                                            className={cn(
                                                              'h-9 w-full pl-3 text-left font-normal',
                                                              !dateField.value &&
                                                                'text-muted-foreground'
                                                            )}
                                                            variant={'outline'}
                                                          >
                                                            {dateField.value ? (
                                                              format(
                                                                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                                                                dateField.value,
                                                                'PPP'
                                                              )
                                                            ) : (
                                                              <span>
                                                                Pick a date
                                                              </span>
                                                            )}
                                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                          </Button>
                                                        </FormControl>
                                                      </PopoverTrigger>
                                                      <PopoverContent
                                                        align="start"
                                                        className="w-auto p-0"
                                                      >
                                                        <Calendar
                                                          captionLayout="dropdown"
                                                          mode="single"
                                                          onSelect={
                                                            dateField.onChange
                                                          }
                                                          selected={
                                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                                            dateField.value
                                                          }
                                                        />
                                                      </PopoverContent>
                                                    </Popover>
                                                  </FormItem>
                                                )}
                                              />
                                            )}
                                          </>
                                        ) : (
                                          <SearchableSelect
                                            onValueChange={field.onChange}
                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                            value={field.value}
                                          >
                                            <FormControl>
                                              <SearchableSelectTrigger className="w-full">
                                                <SearchableSelectValue />
                                              </SearchableSelectTrigger>
                                            </FormControl>
                                            <SearchableSelectContent>
                                              <SearchableSelectGroup>
                                                {temporal.options.map(opt =>
                                                  typeof opt === 'string' ? (
                                                    <SearchableSelectItem
                                                      key={opt}
                                                      value={opt}
                                                    >
                                                      {opt}
                                                    </SearchableSelectItem>
                                                  ) : null
                                                )}
                                              </SearchableSelectGroup>
                                            </SearchableSelectContent>
                                          </SearchableSelect>
                                        )}
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                )
                              )}
                            </div>
                          </div>
                        )}

                      <Button
                        onClick={handleConfirmButtonClick}
                        type="button"
                        variant="brand"
                      >
                        <CheckIcon />
                        Confirm
                      </Button>
                    </form>
                  </Form>
                </div>
              </motion.div>
            ) : task.type === 'sources' ? (
              <motion.div
                className="flex gap-x-2 pb-4"
                key={task.id}
                {...animationProps}
              >
                <div className="bg-outline mx-2 my-0.5 w-0.25" />
                <div className="grow">
                  <div className="text-muted-foreground mb-2 flex items-center gap-x-2">
                    <BookOpenTextIcon size={16} />
                    <div className="text-sm">
                      Reading Sources · {task.data.sources.length}
                    </div>
                  </div>
                  <div className="bg-card rounded-lg border p-2">
                    {task.data.sources.map(source => (
                      <div
                        className="flex gap-x-2 p-2"
                        key={source}
                      >
                        <NotionIcon size={14} />
                        <div className="text-sm leading-none">{source}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                className="flex gap-x-2"
                key={task.id}
                {...animationProps}
              >
                <div className="bg-outline mx-2 my-0.5 w-0.25" />
                <div className="grow">
                  <div className="text-muted-foreground mb-2 flex items-center gap-x-1">
                    <PlayIcon size={16} />
                    <div className="text-sm">
                      Query executed in {task.data.queryExecutionDuration} ·{' '}
                      {task.data.rows.length} rows returned
                    </div>
                  </div>
                  <div className="bg-card rounded-lg border p-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {task.data.columns.map(column => (
                            <TableHead key={column.id}>
                              {column.column}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {task.data.rows.map(row => (
                          <TableRow key={row.id}>
                            {row.cells.map(cell => (
                              <TableCell key={cell.id}>{cell.cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </motion.div>
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuestionAnswer;
